// Thin client for the contactForm Cloud Function (website backend project). Like
// the VAE client, Firebase is imported lazily so it only loads when someone
// actually submits the form, and so the app builds/runs before the backend exists.
//
// IMPORTANT: the site now talks to TWO Firebase projects on one page (VAE +
// website backend). Each is initialized as a distinct NAMED app ("vae" / "site")
// so their configs and App Check instances don't collide on the default app.

import { FIREBASE_CONFIG, RECAPTCHA_SITE_KEY, FUNCTIONS_REGION, CONTACT_CONFIGURED } from "./config";
import { enableAppCheckDebugToken } from "../firebaseDebug";

export type ContactPayload = {
  name: string;
  email: string;
  subject: string;
  message: string;
  contactExtra?: string; // honeypot — left empty by real users
};

export class ContactError extends Error {
  constructor(
    message: string,
    public code: string = "unknown",
  ) {
    super(message);
  }
}

const APP_NAME = "site";

let callablePromise: Promise<(payload: ContactPayload) => Promise<void>> | null = null;

// Build (once) the function that sends a submission to the backend.
async function getCallable(): Promise<(payload: ContactPayload) => Promise<void>> {
  if (!CONTACT_CONFIGURED) {
    throw new ContactError("The contact form isn't connected yet.", "unconfigured");
  }
  if (!callablePromise) {
    callablePromise = (async () => {
      const { initializeApp, getApps } = await import("firebase/app");
      const { initializeAppCheck, ReCaptchaV3Provider } = await import("firebase/app-check");
      const { getFunctions, httpsCallable } = await import("firebase/functions");

      // Reuse the named app if it already exists (e.g. after a hot reload).
      const existing = getApps().find((a) => a.name === APP_NAME);
      const app = existing ?? initializeApp(FIREBASE_CONFIG, APP_NAME);

      // Local dev only — see firebaseDebug.ts. Stripped from production builds.
      enableAppCheckDebugToken(process.env.REACT_APP_SITE_APPCHECK_DEBUG_TOKEN);

      initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(RECAPTCHA_SITE_KEY),
        isTokenAutoRefreshEnabled: true,
      });

      const fn = httpsCallable<ContactPayload, { ok: true }>(
        getFunctions(app, FUNCTIONS_REGION),
        "contactForm",
      );

      return async (payload: ContactPayload) => {
        await fn(payload);
      };
    })();
  }
  return callablePromise;
}

/** Send a contact-form submission. Resolves on success, throws ContactError otherwise. */
export async function sendContactMessage(payload: ContactPayload): Promise<void> {
  try {
    const call = await getCallable();
    await call(payload);
  } catch (e) {
    if (e instanceof ContactError) throw e;
    // Firebase callable errors carry a `code` like "functions/resource-exhausted".
    const err = e as { code?: string; message?: string };
    const code = (err.code ?? "").replace(/^functions\//, "");
    let message: string;
    if (code === "resource-exhausted") {
      message = err.message ?? "Too many messages just now. Please try again later.";
    } else if (code === "invalid-argument") {
      message = err.message ?? "Please check the form and try again.";
    } else {
      message = "Couldn't send your message. Please try again in a moment.";
    }
    throw new ContactError(message, code || "network");
  }
}
